import * as React from "react";
import type { VariantProps } from "class-variance-authority";
import { Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { buttonVariants } from "@/components/ui/buttonVariants";
import { cn } from "@/lib/utils";

type ButtonVariant = VariantProps<typeof buttonVariants>["variant"];
type ButtonSize = VariantProps<typeof buttonVariants>["size"];

type Props = {
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
  capture?: boolean | "user" | "environment";
  onFiles: (files: FileList) => void;
  label?: string;
  /** Shared button variant. Default is secondary (`outline`). Primary CTAs pass `primary`. */
  variant?: ButtonVariant;
  /** Shared button size. Icon-only pickers default to `icon`; labeled ones to `default`. */
  size?: ButtonSize;
  /** Ha megadod, a gomb aria-label-je (ikon-only gombokhoz). */
  "aria-label"?: string;
  className?: string;
  "data-testid"?: string;
};

function mergeRefs<T>(...refs: Array<React.Ref<T> | undefined>) {
  return (value: T | null) => {
    for (const ref of refs) {
      if (!ref) continue;
      if (typeof ref === "function") ref(value);
      else (ref as React.MutableRefObject<T | null>).current = value;
    }
  };
}

/** Natív „Choose File” helyett magyar feliratú gomb (F1.2). */
export const FileUploadButton = React.forwardRef<HTMLInputElement, Props>(function FileUploadButton(
  {
    accept,
    multiple,
    disabled,
    capture,
    onFiles,
    label = "Fájl kiválasztása",
    variant = "secondary",
    size,
    "aria-label": ariaLabel,
    className,
    "data-testid": testId
  },
  forwardedRef
) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const iconOnly = label.trim() === "";
  const stretch = Boolean(className && /(^|\s)w-full(\s|$)/.test(className));
  const resolvedSize = size ?? (iconOnly ? "icon" : "default");

  return (
    <span
      className={cn(
        "relative inline-flex min-w-0 max-w-full [&_button]:max-w-full",
        stretch && "w-full"
      )}
    >
      <input
        ref={mergeRefs(inputRef, forwardedRef)}
        type="file"
        accept={accept}
        multiple={multiple}
        disabled={disabled}
        capture={capture === true ? "environment" : capture || undefined}
        className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          if (e.target.files?.length) onFiles(e.target.files);
          e.target.value = "";
        }}
      />
      <Button
        type="button"
        variant={variant}
        size={resolvedSize}
        disabled={disabled}
        className={cn("min-w-0 max-w-full", className)}
        data-testid={testId}
        aria-label={ariaLabel ?? (iconOnly ? "Fájl feltöltése" : undefined)}
        title={ariaLabel ?? (iconOnly ? "Fájl feltöltése" : undefined)}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="h-4 w-4 shrink-0" aria-hidden />
        {iconOnly ? (
          <span className="sr-only">{ariaLabel ?? "Fájl feltöltése"}</span>
        ) : (
          <span className="truncate">{label}</span>
        )}
      </Button>
    </span>
  );
});
